import { COUNTRIES_BY_CODE } from "@redotech/locale/countries";
import { USA_PROVINCES_BY_CODE } from "@redotech/locale/provinces";
import { assertNever } from "@redotech/util/type";
import { ConversationPlatform } from "../conversation";
import { Provider } from "../order";
import { Channel as ReturnChannel, ReturnTypeEnum } from "../return";
import { StepValidationResult } from "../return-flow-step-valid";
import { PurchaseChannel } from "../warranties";
import {
  AdjustmentAmountOption,
  AssignmentTypeOption,
  Condition,
  DateOption,
  DiscountMatchType,
  DiscountOptionModel,
  ItemDiscountMeasureOption,
  LoyaltyEarnedMeasureOption,
  MetafieldMatchType,
  NumClaimsOption,
  NumItemsBeingReturnedOption,
  NumOrdersOption,
  NumReturnsOption,
  PaymentMethodOption,
  PriceOption,
  ReturnRateOption,
  SkuOption,
  SubjectLineMatchType,
} from "./condition";
import { DateSubcondition, DateSubconditionType } from "./date-subcondition";
import { ReturnOptionMethod } from "./return-option";

// ── Shared helpers ────────────────────────────────────────────────────────────

/** State for conditions with no meaningful configuration (flag-style). */
export type EmptyConditionState = Record<string, never>;

function emptyFromModel(): EmptyConditionState {
  return {};
}

function validateEmpty(_state: EmptyConditionState): StepValidationResult {
  return { valid: true };
}

// ── DateSubconditionState ─────────────────────────────────────────────────────

export interface DateSubconditionState {
  type: DateSubconditionType;
  beforeDate: Date | null;
  afterDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  days: number | null;
}

export const emptyDateSubconditionState: DateSubconditionState = {
  type: DateSubconditionType.ALL_TIME,
  beforeDate: null,
  afterDate: null,
  startDate: null,
  endDate: null,
  days: 30,
};

export function fromModelDateSubcondition(
  model: DateSubcondition,
): DateSubconditionState {
  const result: DateSubconditionState = {
    type: model.type || DateSubconditionType.ALL_TIME,
    beforeDate: null,
    afterDate: null,
    startDate: null,
    endDate: null,
    days: null,
  };
  switch (result.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      result.beforeDate = model.beforeDate ? new Date(model.beforeDate) : null;
      break;
    case DateSubconditionType.ON_OR_AFTER:
      result.afterDate = model.afterDate ? new Date(model.afterDate) : null;
      break;
    case DateSubconditionType.BETWEEN:
      result.startDate = model.startDate ? new Date(model.startDate) : null;
      result.endDate = model.endDate ? new Date(model.endDate) : null;
      break;
    case DateSubconditionType.LAST_N_DAYS:
      result.days = model.days || null;
      break;
  }
  return result;
}

export function validateDateSubcondition(
  state: DateSubconditionState,
): StepValidationResult {
  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      return state.beforeDate
        ? { valid: true }
        : { valid: false, errors: ["Before date is required"] };
    case DateSubconditionType.ON_OR_AFTER:
      return state.afterDate
        ? { valid: true }
        : { valid: false, errors: ["After date is required"] };
    case DateSubconditionType.BETWEEN: {
      const errors: string[] = [];
      if (!state.startDate) errors.push("Start date is required");
      if (!state.endDate) errors.push("End date is required");
      return errors.length ? { valid: false, errors } : { valid: true };
    }
    case DateSubconditionType.LAST_N_DAYS:
      return state.days && state.days > 0
        ? { valid: true }
        : { valid: false, errors: ["Number of days must be greater than 0"] };
    case DateSubconditionType.ALL_TIME:
      return { valid: true };
  }
}

// ── State types ───────────────────────────────────────────────────────────────

export type CoverageConditionState = EmptyConditionState;
export type PackageProtectionConditionState = EmptyConditionState;
export type AfterHoursConditionState = EmptyConditionState;
export type CustomerEmailSubscriberConditionState = EmptyConditionState;
export type CustomerSmsSubscriberConditionState = EmptyConditionState;
export type OrderlessConditionState = EmptyConditionState;
export type MerchantCreatedConditionState = EmptyConditionState;
export type ExtendedWarrantyConditionState = EmptyConditionState;
export type ExtendedWarrantyPurchasedConditionState = EmptyConditionState;

export type CustomerTagConditionState = readonly string[];
export type CustomerEmailConditionState = readonly string[];
export type OrderTagConditionState = readonly string[];
export type CollectionConditionState = string[];
export type OrderTimeCollectionConditionState = string[];
export type ProductPropertyConditionState = readonly string[];
export type ProductTagConditionState = readonly string[];
export type ProductTypeConditionState = readonly string[];
export type SalesChannelConditionState = readonly string[];
export type VendorConditionState = readonly string[];
export type OrderNumberConditionState = readonly string[];
export type TicketChannelConditionState = ConversationPlatform[];
export type TopicConditionState = string[];
export type ReceivedAtEmailConditionState = string[];

/** Flag conditions — fromModel returns true, valid always returns true. */
export type DamagedConditionState = boolean;
export type IsRepairConditionState = boolean;
export type WrongProductConditionState = boolean;

export type IsRefundReturnConditionState = string;
export type TrackingTypeConditionState = string;
export type OrderCountGreaterThanConditionState = string;
export type TotalSpentGreaterThanConditionState = string;
export type DeliveryWithinConditionState = string;
export type FulfillmentWithinConditionState = string;
export type OrderWithinConditionState = string;
export type FulfillmentTrackingUpdateWithinConditionState = string;
export type ExchangeCountConditionState = string;
export type PaymentMethodConditionState = PaymentMethodOption | null;
export type PlatformConditionState = Provider | null;
export type ChannelConditionState = ReturnChannel | undefined;

export interface FinalSaleReturnConditionState {
  validCollections: readonly string[];
  validProductTags: readonly string[];
}

export interface DiscountConditionState {
  codes: readonly string[];
  codeOption: DiscountOptionModel | null;
  matchType: DiscountMatchType | null;
}

export interface ItemDiscountConditionState {
  measureOption: ItemDiscountMeasureOption;
  value: number;
}

export interface LoyaltyEarnedConditionState {
  measureOption: LoyaltyEarnedMeasureOption;
  value: number;
}

export interface SkuConditionState {
  codes: readonly string[];
  skuOption: SkuOption | null;
}

export interface AdjustmentAmountConditionState {
  adjustmentAmountOption: AdjustmentAmountOption;
  adjustmentAmount: string;
}

export interface CompensationMethodConditionState {
  compensationMethod: ReturnOptionMethod["type"];
}

export interface ReturnMethodConditionState {
  returnMethod: ReturnOptionMethod["type"];
}

export interface PurchaseChannelConditionState {
  channel: PurchaseChannel;
}

export interface AssignmentTypeConditionState {
  assignmentType: AssignmentTypeOption;
}

export interface ItemsBeingReturnedCountConditionState {
  numItemsOption: NumItemsBeingReturnedOption;
  numItems: string;
}

export type CustomerCountryConditionState = readonly string[];
export interface CustomerAddressConditionState {
  country: string;
  provinceCodes: readonly string[];
}

export interface DateConditionState {
  start: Date | null;
  end: Date | null;
  dateOption: DateOption | null;
}

export interface LocationNameConditionState {
  names: string[];
}

export interface MultipleChoiceConditionState {
  questionText: string;
  answers: string[];
}

export interface PriceConditionState {
  amount: string;
  priceOption: PriceOption | null;
}

export interface NumCustomerClaimsConditionState {
  numClaims: string;
  claimOption: NumClaimsOption | null;
}

export interface NumCustomerReturnsConditionState {
  numReturns: string;
  returnOption: NumReturnsOption | null;
  dateSubcondition: DateSubconditionState;
}

export interface NumOrdersConditionState {
  numOrders: string;
  orderOption: NumOrdersOption | null;
  dateSubcondition: DateSubconditionState;
}

export interface ReturnRateConditionState {
  returnRate: string;
  comparison: ReturnRateOption | null;
}

export type ReturnReasonConditionState = readonly string[];

export interface ReturnTypeConditionState {
  returnTypes: ReturnTypeEnum[];
}

export type ProductVariantMetafieldConditionState = Omit<
  Condition.ProductVariantMetafield,
  "type"
>;

export interface SentFromEmailConditionState {
  emails: string[];
  matchMode: "INCLUDES" | "IS_EXACTLY";
}

export interface SubjectLineConditionState {
  fuzzy_match: string;
  queryStrings: string[];
  caseSensitive: boolean;
  matchMode: SubjectLineMatchType;
}

export interface MessageBodyMatchesConditionState {
  queryStrings?: string[];
  caseSensitive: boolean;
  matchMode: "INCLUDES" | "IS_EXACTLY";
}

// ── Type aliases matching UI file state type names ────────────────────────────

export type AdjustmentAmountState = AdjustmentAmountConditionState;
export type AfterHoursState = AfterHoursConditionState;
export type ChannelState = ChannelConditionState;
export type CollectionState = CollectionConditionState;
export type CompensationMethodState = CompensationMethodConditionState;
export type CoverageState = CoverageConditionState;
export type CustomerAddressState = CustomerAddressConditionState;
export type CustomerCountryState = CustomerCountryConditionState;
export type CustomerEmailSubscriberState =
  CustomerEmailSubscriberConditionState;
export type CustomerEmailState = CustomerEmailConditionState;
export type CustomerSmsSubscriberState = CustomerSmsSubscriberConditionState;
export type CustomerTagState = CustomerTagConditionState;
export type DamagedState = DamagedConditionState;
export type DeliveryWithinState = DeliveryWithinConditionState;
export type DiscountState = DiscountConditionState;
export type ExchangeCountState = ExchangeCountConditionState;
export type ExtendedWarrantyPurchasedState =
  ExtendedWarrantyPurchasedConditionState;
export type ExtendedWarrantyState = ExtendedWarrantyConditionState;
export type FinalSaleReturnState = FinalSaleReturnConditionState;
export type FulfillmentTrackingUpdateWithinState =
  FulfillmentTrackingUpdateWithinConditionState;
export type FulfillmentWithinState = FulfillmentWithinConditionState;
export type IsRefundReturnState = IsRefundReturnConditionState;
export type IsRepairState = IsRepairConditionState;
export type ItemDiscountState = ItemDiscountConditionState;
export type ItemsBeingReturnedCountState =
  ItemsBeingReturnedCountConditionState;
export type LocationNameState = LocationNameConditionState;
export type LoyaltyEarnedState = LoyaltyEarnedConditionState;
export type MerchantCreatedState = MerchantCreatedConditionState;
export type MultipleChoiceState = MultipleChoiceConditionState;
export type NonReturnedValueState = PriceConditionState;
export type NumCustomerClaimsState = NumCustomerClaimsConditionState;
export type NumCustomerReturnsState = NumCustomerReturnsConditionState;
export type NumOrdersState = NumOrdersConditionState;
export type OrderCountGreaterThanState = OrderCountGreaterThanConditionState;
export type OrderNumberState = OrderNumberConditionState;
export type OrderTagState = OrderTagConditionState;
export type OrderTimeCollectionState = OrderTimeCollectionConditionState;
export type OrderWithinState = OrderWithinConditionState;
export type OrderlessState = OrderlessConditionState;
export type PackageProtectionState = PackageProtectionConditionState;
export type PaymentMethodState = PaymentMethodConditionState;
export type PlatformState = PlatformConditionState;
export type PriceState = PriceConditionState;
export type ProductPropertyState = ProductPropertyConditionState;
export type ProductTagState = ProductTagConditionState;
export type ProductTypeState = ProductTypeConditionState;
export type ProductVariantMetafieldState =
  ProductVariantMetafieldConditionState;
export type PurchaseChannelState = PurchaseChannelConditionState;
export type ReturnMethodState = ReturnMethodConditionState;
export type ReturnRateState = ReturnRateConditionState;
export type ReturnReasonState = ReturnReasonConditionState;
export type ReturnTypeState = ReturnTypeConditionState;
export type ReturnValueState = PriceConditionState;
export type SalesChannelState = SalesChannelConditionState;
export type SkuState = SkuConditionState;
export type TotalPreDiscountPriceState = PriceConditionState;
export type TotalReturnValueState = PriceConditionState;
export type TotalSpentGreaterThanState = TotalSpentGreaterThanConditionState;
export type TrackingTypeState = TrackingTypeConditionState;
export type VendorState = VendorConditionState;
export type WrongProductState = WrongProductConditionState;

// ── fromModel functions ───────────────────────────────────────────────────────

export function coverageFromModel(
  _model: Condition.Coverage,
): CoverageConditionState {
  return emptyFromModel();
}
export function packageProtectionFromModel(
  _model: Condition.PackageProtection,
): PackageProtectionConditionState {
  return emptyFromModel();
}
export function afterHoursFromModel(
  _model: Condition.AfterHours,
): AfterHoursConditionState {
  return emptyFromModel();
}
export function customerEmailSubscriberFromModel(
  _model: Condition.CustomerEmailSubscriber,
): CustomerEmailSubscriberConditionState {
  return emptyFromModel();
}
export function customerSmsSubscriberFromModel(
  _model: Condition.CustomerSmsSubscriber,
): CustomerSmsSubscriberConditionState {
  return emptyFromModel();
}
export function orderlessFromModel(
  _model: Condition.Orderless,
): OrderlessConditionState {
  return emptyFromModel();
}
export function merchantCreatedFromModel(
  _model: Condition.MerchantCreated,
): MerchantCreatedConditionState {
  return emptyFromModel();
}
export function extendedWarrantyFromModel(
  _model: Condition.ExtendedWarranty,
): ExtendedWarrantyConditionState {
  return emptyFromModel();
}
export function extendedWarrantyPurchasedFromModel(
  _model: Condition.ExtendedWarrantyPurchased,
): ExtendedWarrantyPurchasedConditionState {
  return emptyFromModel();
}

export function customerTagFromModel(
  model: Condition.CustomerTag,
): CustomerTagConditionState {
  return model.tags;
}
export function customerEmailFromModel(
  model: Condition.CustomerEmail,
): CustomerEmailConditionState {
  return model.emails;
}
export function orderTagFromModel(
  model: Condition.OrderTag,
): OrderTagConditionState {
  return model.tags;
}
export function collectionFromModel(
  model: Condition.Collection,
): CollectionConditionState {
  return model.collections;
}
export function orderTimeCollectionFromModel(
  model: Condition.OrderTimeCollection,
): OrderTimeCollectionConditionState {
  return model.collections;
}
export function productPropertyFromModel(
  model: Condition.ProductProperty,
): ProductPropertyConditionState {
  return model.properties;
}
export function productTagFromModel(
  model: Condition.ProductTag,
): ProductTagConditionState {
  return model.tags;
}
export function productTypeFromModel(
  model: Condition.ProductType,
): ProductTypeConditionState {
  return model.productTypes;
}
export function salesChannelFromModel(
  model: Condition.SalesChannel,
): SalesChannelConditionState {
  return model.channels;
}
export function vendorFromModel(model: Condition.Vendor): VendorConditionState {
  return model.vendors;
}
export function orderNumberFromModel(
  model: Condition.OrderNumber,
): OrderNumberConditionState {
  return model.orderNumbers;
}
export function ticketChannelFromModel(
  model: Condition.TicketChannel,
): TicketChannelConditionState {
  return model.channels;
}
export function topicFromModel(model: Condition.Topic): TopicConditionState {
  return model.topics;
}
export function receivedAtEmailFromModel(
  model: Condition.ReceivedAtEmail,
): ReceivedAtEmailConditionState {
  return model.emails;
}

export function damagedFromModel(
  _model: Condition.Damaged,
): DamagedConditionState {
  return true;
}
export function isRepairFromModel(
  _model: Condition.IsRepair,
): IsRepairConditionState {
  return true;
}
export function wrongProductFromModel(
  _model: Condition.WrongProduct,
): WrongProductConditionState {
  return true;
}

export function isRefundReturnFromModel(
  _model: Condition.IsRefundReturn,
): IsRefundReturnConditionState {
  return "";
}
export function trackingTypeFromModel(
  model: Condition.TrackingType,
): TrackingTypeConditionState {
  return model.trackingType;
}
export function orderCountGreaterThanFromModel(
  model: Condition.OrderCountGreaterThan,
): OrderCountGreaterThanConditionState {
  return model.orderCount;
}
export function totalSpentGreaterThanFromModel(
  model: Condition.TotalSpentGreaterThan,
): TotalSpentGreaterThanConditionState {
  return model.totalSpent;
}
export function deliveryWithinFromModel(
  model: Condition.ShippingWithin,
): DeliveryWithinConditionState {
  return String(model.duration.total("days"));
}
export function fulfillmentWithinFromModel(
  model: Condition.FulfillmentPeriod,
): FulfillmentWithinConditionState {
  return String(model.duration.total("days"));
}
export function orderWithinFromModel(
  model: Condition.OrderWithin,
): OrderWithinConditionState {
  return String(model.duration.total("days"));
}
export function fulfillmentTrackingUpdateWithinFromModel(
  model: Condition.FulfillmentTrackingUpdateWithin,
): FulfillmentTrackingUpdateWithinConditionState {
  return model.duration.total("days").toFixed(0);
}
export function exchangeCountFromModel(
  model: Condition.ExchangeCount,
): ExchangeCountConditionState {
  return model.numExchanges.toFixed(0);
}
export function paymentMethodFromModel(
  model: Condition.PaymentMethod,
): PaymentMethodConditionState {
  return model.method;
}
export function platformFromModel(
  model: Condition.Platform,
): PlatformConditionState {
  return model.method;
}
export function channelFromModel(
  model: Condition.Channel,
): ChannelConditionState {
  return model.channel;
}

export function finalSaleReturnFromModel(
  model: Condition.FinalSaleReturn,
): FinalSaleReturnConditionState {
  return {
    validCollections: model.validCollections,
    validProductTags: model.validProductTags,
  };
}

export function discountFromModel(
  model: Condition.Discount,
): DiscountConditionState {
  return {
    codes: model.codes,
    codeOption: model.codeOption,
    matchType: model.matchType || DiscountMatchType.EXACT,
  };
}

export function itemDiscountFromModel(
  model: Condition.ItemDiscount,
): ItemDiscountConditionState {
  return { measureOption: model.measureOption, value: model.value };
}

export function loyaltyEarnedFromModel(
  model: Condition.LoyaltyEarned,
): LoyaltyEarnedConditionState {
  return { measureOption: model.measureOption, value: model.value };
}

export function skuFromModel(model: Condition.Sku): SkuConditionState {
  return { codes: model.codes, skuOption: model.skuOption || null };
}

export function adjustmentAmountFromModel(
  model: Condition.AdjustmentAmount,
): AdjustmentAmountConditionState {
  return {
    adjustmentAmountOption: model.adjustmentAmountOption,
    adjustmentAmount: model.adjustmentAmount.toFixed(2),
  };
}

export function compensationMethodFromModel(
  model: Condition.CompensationMethod,
): CompensationMethodConditionState {
  return { compensationMethod: model.compensationMethod };
}

export function returnMethodFromModel(
  model: Condition.ReturnMethod,
): ReturnMethodConditionState {
  return { returnMethod: model.returnMethod };
}

export function purchaseChannelConditionFromModel(
  model: Condition.PurchaseChannel,
): PurchaseChannelConditionState {
  return { channel: model.channel as PurchaseChannel };
}

export function assignmentTypeFromModel(
  model: Condition.AssignmentType,
): AssignmentTypeConditionState {
  return { assignmentType: model.assignmentType };
}

export function itemsBeingReturnedCountFromModel(
  model: Condition.ItemsBeingReturnedCount,
): ItemsBeingReturnedCountConditionState {
  return {
    numItemsOption: model.numItemsOption,
    numItems: model.numItems.toFixed(0),
  };
}

export function customerCountryFromModel(
  model: Condition.CustomerCountry,
): CustomerCountryConditionState {
  return model.countries;
}

export function customerAddressFromModel(
  model: Condition.CustomerAddress,
): CustomerAddressConditionState {
  return { country: model.country, provinceCodes: model.provinceCodes };
}

export function orderDateFromModel(
  model: Condition.OrderDate,
): DateConditionState {
  return {
    start: model.start
      ? new Date(
          model.start.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        )
      : null,
    end: model.end
      ? new Date(
          model.end.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        )
      : null,
    dateOption: model.dateOption,
  };
}

export function returnDateFromModel(
  model: Condition.ReturnDate,
): DateConditionState {
  return {
    start: model.start
      ? new Date(
          model.start.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        )
      : null,
    end: model.end
      ? new Date(
          model.end.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        )
      : null,
    dateOption: model.dateOption,
  };
}

export function locationNameFromModel(
  model: Condition.LocationName,
): LocationNameConditionState {
  return { names: model.names };
}

export function multipleChoiceConditionFromModel(
  model: Condition.MultipleChoice,
): MultipleChoiceConditionState {
  return { questionText: model.questionText, answers: model.answers };
}

export function priceConditionFromModel(model: {
  amount: number;
  priceOption: PriceOption | null;
}): PriceConditionState {
  return { amount: model.amount.toFixed(2), priceOption: model.priceOption };
}

export function numCustomerClaimsFromModel(
  model: Condition.NumCustomerClaims,
): NumCustomerClaimsConditionState {
  return {
    numClaims: model.numClaims.toFixed(0),
    claimOption: model.numClaimsOption,
  };
}

export function numCustomerReturnsFromModel(
  model: Condition.NumCustomerReturns,
): NumCustomerReturnsConditionState {
  return {
    numReturns: model.numReturns.toFixed(0),
    returnOption: model.numReturnsOption,
    // Use dateSubcondition directly
    dateSubcondition: model.dateSubcondition
      ? fromModelDateSubcondition(model.dateSubcondition)
      : emptyDateSubconditionState,
  };
}

export function numOrdersFromModel(
  model: Condition.NumOrders,
): NumOrdersConditionState {
  return {
    numOrders: model.numOrders.toFixed(0),
    orderOption: model.numOrdersOption,
    // Convert dateSubcondition or migrate from legacy fields
    dateSubcondition: model.dateSubcondition
      ? fromModelDateSubcondition(model.dateSubcondition)
      : emptyDateSubconditionState,
  };
}

export function returnRateFromModel(
  model: Condition.ReturnRate,
): ReturnRateConditionState {
  return {
    returnRate: (+model.returnRate.toString() * 100).toFixed(0),
    comparison: model.comparison,
  };
}

export function returnReasonFromModel(
  model: Condition.ReturnReason,
): ReturnReasonConditionState {
  return model.reasons;
}

export function returnTypeConditionFromModel(
  model: Condition.ReturnType,
): ReturnTypeConditionState {
  return { returnTypes: model.returnTypes };
}

export function productVariantMetafieldFromModel(
  model: Condition.ProductVariantMetafield,
): ProductVariantMetafieldConditionState {
  return { ...model };
}

export function sentFromEmailFromModel(
  model: Condition.SentFromEmail,
): SentFromEmailConditionState {
  return { emails: model.emails, matchMode: model.matchMode };
}

export function subjectLineFromModel(
  model: Condition.SubjectLine,
): SubjectLineConditionState {
  return {
    fuzzy_match: model.fuzzy_match,
    queryStrings: model.queryStrings || [model.fuzzy_match || ""],
    caseSensitive: model.caseSensitive ?? false,
    matchMode: model.matchMode ?? SubjectLineMatchType.CONTAINS,
  };
}

export function messageBodyMatchesFromModel(
  model: Condition.MessageBodyMatches,
): MessageBodyMatchesConditionState {
  return {
    queryStrings: model.queryStrings || [model.queryString || ""],
    caseSensitive: model.caseSensitive,
    matchMode: model.matchMode,
  };
}

// ── valid functions ───────────────────────────────────────────────────────────

export function validateCoverageCondition(
  state: CoverageConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validatePackageProtectionCondition(
  state: PackageProtectionConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateAfterHoursCondition(
  state: AfterHoursConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateCustomerEmailSubscriberCondition(
  state: CustomerEmailSubscriberConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateCustomerSmsSubscriberCondition(
  state: CustomerSmsSubscriberConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateOrderlessCondition(
  state: OrderlessConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateMerchantCreatedCondition(
  state: MerchantCreatedConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateExtendedWarrantyCondition(
  state: ExtendedWarrantyConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateExtendedWarrantyPurchasedCondition(
  state: ExtendedWarrantyPurchasedConditionState,
): StepValidationResult {
  return validateEmpty(state);
}
export function validateCustomerTagCondition(
  _state: CustomerTagConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateCustomerEmailCondition(
  _state: CustomerEmailConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateOrderTagCondition(
  _state: OrderTagConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateCollectionCondition(
  _state: CollectionConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateOrderTimeCollectionCondition(
  _state: OrderTimeCollectionConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateProductPropertyCondition(
  _state: ProductPropertyConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateProductTagCondition(
  _state: ProductTagConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateProductTypeCondition(
  _state: ProductTypeConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateSalesChannelCondition(
  _state: SalesChannelConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateVendorCondition(
  _state: VendorConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateOrderNumberCondition(
  _state: OrderNumberConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateTicketChannelCondition(
  _state: TicketChannelConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateTopicCondition(
  _state: TopicConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateDamagedCondition(
  _state: DamagedConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateIsRepairCondition(
  _state: IsRepairConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateWrongProductCondition(
  _state: WrongProductConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateIsRefundReturnCondition(
  _state: IsRefundReturnConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateTrackingTypeCondition(
  _state: TrackingTypeConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateOrderCountGreaterThanCondition(
  _state: OrderCountGreaterThanConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateTotalSpentGreaterThanCondition(
  _state: TotalSpentGreaterThanConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateFinalSaleReturnCondition(
  _state: FinalSaleReturnConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateDiscountCondition(
  _state: DiscountConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateItemDiscountCondition(
  _state: ItemDiscountConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateLoyaltyEarnedCondition(
  _state: LoyaltyEarnedConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateSkuCondition(
  _state: SkuConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateAdjustmentAmountCondition(
  _state: AdjustmentAmountConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateSubjectLineCondition(
  _state: SubjectLineConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validateAssignmentTypeCondition(
  _state: AssignmentTypeConditionState,
): StepValidationResult {
  return { valid: true };
}

export function validateChannelCondition(
  state: ChannelConditionState,
): StepValidationResult {
  return state !== undefined
    ? { valid: true }
    : { valid: false, errors: ["Channel is required"] };
}

export function validatePlatformCondition(
  _state: PlatformConditionState,
): StepValidationResult {
  return { valid: true };
}
export function validatePaymentMethodCondition(
  _state: PaymentMethodConditionState,
): StepValidationResult {
  return { valid: true };
}

export function validateCompensationMethodCondition(
  state: CompensationMethodConditionState,
): StepValidationResult {
  return state.compensationMethod !== undefined
    ? { valid: true }
    : { valid: false, errors: ["Compensation method is required"] };
}

export function validateReturnMethodCondition(
  state: ReturnMethodConditionState,
): StepValidationResult {
  return state.returnMethod !== undefined
    ? { valid: true }
    : { valid: false, errors: ["Return method is required"] };
}

export function validatePurchaseChannelCondition(
  state: PurchaseChannelConditionState,
): StepValidationResult {
  return state.channel
    ? { valid: true }
    : { valid: false, errors: ["Purchase channel is required"] };
}

export function validateReceivedAtEmailCondition(
  state: ReceivedAtEmailConditionState,
): StepValidationResult {
  return !state.includes("No email addresses configured")
    ? { valid: true }
    : { valid: false, errors: ["No email addresses configured"] };
}

export function validateSentFromEmailCondition(
  state: SentFromEmailConditionState,
): StepValidationResult {
  return state.emails.length > 0
    ? { valid: true }
    : { valid: false, errors: ["At least one email address is required"] };
}

export function validateMessageBodyMatchesCondition(
  state: MessageBodyMatchesConditionState,
): StepValidationResult {
  return (state.queryStrings?.length || 0) > 0
    ? { valid: true }
    : { valid: false, errors: ["At least one query string is required"] };
}

export function validateDeliveryWithinCondition(
  state: DeliveryWithinConditionState,
): StepValidationResult {
  return +state >= 0
    ? { valid: true }
    : {
        valid: false,
        errors: ["Delivery window must be a non-negative number"],
      };
}

export function validateFulfillmentWithinCondition(
  state: FulfillmentWithinConditionState,
): StepValidationResult {
  return +state >= 0
    ? { valid: true }
    : {
        valid: false,
        errors: ["Fulfillment window must be a non-negative number"],
      };
}

export function validateOrderWithinCondition(
  state: OrderWithinConditionState,
): StepValidationResult {
  return +state >= 0
    ? { valid: true }
    : { valid: false, errors: ["Order window must be a non-negative number"] };
}

export function validateFulfillmentTrackingUpdateWithinCondition(
  state: FulfillmentTrackingUpdateWithinConditionState,
): StepValidationResult {
  return +state >= 0 && +state % 1 === 0
    ? { valid: true }
    : { valid: false, errors: ["Days must be a non-negative whole number"] };
}

export function validateExchangeCountCondition(
  state: ExchangeCountConditionState,
): StepValidationResult {
  return +state >= 1 && +state % 1 === 0
    ? { valid: true }
    : {
        valid: false,
        errors: ["Exchange count must be a positive whole number"],
      };
}

export function validateItemsBeingReturnedCountCondition(
  state: ItemsBeingReturnedCountConditionState,
): StepValidationResult {
  return +state.numItems >= 0 && +state.numItems % 1 === 0
    ? { valid: true }
    : {
        valid: false,
        errors: ["Number of items must be a non-negative whole number"],
      };
}

export function validateCustomerCountryCondition(
  state: CustomerCountryConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.length === 0) {
    errors.push("At least one country is required");
  } else if (!state.every((c) => !!COUNTRIES_BY_CODE[c])) {
    errors.push("One or more invalid country codes");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateCustomerAddressCondition(
  state: CustomerAddressConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.country || !COUNTRIES_BY_CODE[state.country]) {
    errors.push("A valid country is required");
  }
  if (state.provinceCodes.length === 0) {
    errors.push("At least one province is required");
  } else if (
    state.country === "US" &&
    !state.provinceCodes.every((code) => !!USA_PROVINCES_BY_CODE[code])
  ) {
    errors.push("One or more invalid province codes");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateDateConditionState(
  state: DateConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.dateOption === null) {
    errors.push("Date option is required");
  } else if (
    state.dateOption === DateOption.AFTER ||
    state.dateOption === DateOption.BEFORE
  ) {
    if (!state.start) errors.push("Date is required");
  } else {
    if (!state.start) errors.push("Start date is required");
    if (!state.end) errors.push("End date is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateLocationNameCondition(
  state: LocationNameConditionState,
): StepValidationResult {
  return state.names.length > 0
    ? { valid: true }
    : { valid: false, errors: ["At least one location name is required"] };
}

export function validateMultipleChoiceCondition(
  state: MultipleChoiceConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.questionText.length === 0) errors.push("Question text is required");
  if (state.answers.length === 0)
    errors.push("At least one answer is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validatePriceCondition(
  state: PriceConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (+state.amount < 0) errors.push("Amount must be a non-negative number");
  if (state.priceOption === null) errors.push("Price option is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateNumCustomerClaimsCondition(
  state: NumCustomerClaimsConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.claimOption === null) errors.push("Claim option is required");
  if (+state.numClaims < 0)
    errors.push("Number of claims must be a non-negative number");
  if (+state.numClaims % 1 !== 0)
    errors.push("Number of claims must be a whole number");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateNumCustomerReturnsCondition(
  state: NumCustomerReturnsConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.returnOption === null) errors.push("Return option is required");
  if (+state.numReturns < 0)
    errors.push("Number of returns must be a non-negative number");
  if (+state.numReturns % 1 !== 0)
    errors.push("Number of returns must be a whole number");
  const dateResult = validateDateSubcondition(state.dateSubcondition);
  if (!dateResult.valid) errors.push(...dateResult.errors);
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateNumOrdersCondition(
  state: NumOrdersConditionState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.orderOption === null) errors.push("Order option is required");
  if (+state.numOrders < 0)
    errors.push("Number of orders must be a non-negative number");
  if (+state.numOrders % 1 !== 0)
    errors.push("Number of orders must be a whole number");
  const dateResult = validateDateSubcondition(state.dateSubcondition);
  if (!dateResult.valid) errors.push(...dateResult.errors);
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function validateReturnRateCondition(
  state: ReturnRateConditionState,
): StepValidationResult {
  return state.comparison !== null
    ? { valid: true }
    : { valid: false, errors: ["Comparison is required"] };
}

export function validateReturnReasonCondition(
  state: ReturnReasonConditionState,
): StepValidationResult {
  return state.length > 0
    ? { valid: true }
    : { valid: false, errors: ["At least one return reason is required"] };
}

export function validateReturnTypeCondition(
  state: ReturnTypeConditionState,
): StepValidationResult {
  return state.returnTypes.length > 0
    ? { valid: true }
    : { valid: false, errors: ["At least one return type is required"] };
}

export function validateProductVariantMetafieldCondition(
  state: ProductVariantMetafieldConditionState,
): StepValidationResult {
  if (state.comparison === MetafieldMatchType.REGEX) {
    try {
      new RegExp(state.value);
    } catch {
      return { valid: false, errors: ["Invalid regular expression"] };
    }
  }
  return { valid: true };
}

// ── Dispatcher ────────────────────────────────────────────────────────────────

/**
 * Validates a parsed Condition model value against business logic constraints.
 * Each condition type has a corresponding fromModel + valid function pair above,
 * following the same pattern used by all other step types.
 */
export function validateCondition(condition: Condition): StepValidationResult {
  switch (condition.type) {
    case Condition.COVERAGE:
      return validateCoverageCondition(coverageFromModel(condition));
    case Condition.PACKAGE_PROTECTION:
      return validatePackageProtectionCondition(
        packageProtectionFromModel(condition),
      );
    case Condition.AFTER_HOURS:
      return validateAfterHoursCondition(afterHoursFromModel(condition));
    case Condition.CUSTOMER_EMAIL_SUBSCRIBER:
      return validateCustomerEmailSubscriberCondition(
        customerEmailSubscriberFromModel(condition),
      );
    case Condition.CUSTOMER_SMS_SUBSCRIBER:
      return validateCustomerSmsSubscriberCondition(
        customerSmsSubscriberFromModel(condition),
      );
    case Condition.ORDERLESS:
      return validateOrderlessCondition(orderlessFromModel(condition));
    case Condition.MERCHANT_CREATED:
      return validateMerchantCreatedCondition(
        merchantCreatedFromModel(condition),
      );
    case Condition.EXTENDED_WARRANTY:
      return validateExtendedWarrantyCondition(
        extendedWarrantyFromModel(condition),
      );
    case Condition.EXTENDED_WARRANTY_PURCHASED:
      return validateExtendedWarrantyPurchasedCondition(
        extendedWarrantyPurchasedFromModel(condition),
      );
    case Condition.CUSTOMER_TAG:
      return validateCustomerTagCondition(customerTagFromModel(condition));
    case Condition.CUSTOMER_EMAIL:
      return validateCustomerEmailCondition(customerEmailFromModel(condition));
    case Condition.ORDER_TAG:
      return validateOrderTagCondition(orderTagFromModel(condition));
    case Condition.COLLECTION:
      return validateCollectionCondition(collectionFromModel(condition));
    case Condition.ORDER_TIME_COLLECTION:
      return validateOrderTimeCollectionCondition(
        orderTimeCollectionFromModel(condition),
      );
    case Condition.PRODUCT_PROPERTY:
      return validateProductPropertyCondition(
        productPropertyFromModel(condition),
      );
    case Condition.PRODUCT_TAG:
      return validateProductTagCondition(productTagFromModel(condition));
    case Condition.PRODUCT_TYPE:
      return validateProductTypeCondition(productTypeFromModel(condition));
    case Condition.SALES_CHANNEL:
      return validateSalesChannelCondition(salesChannelFromModel(condition));
    case Condition.VENDOR:
      return validateVendorCondition(vendorFromModel(condition));
    case Condition.ORDER_NUMBER:
      return validateOrderNumberCondition(orderNumberFromModel(condition));
    case Condition.TICKET_CHANNEL:
      return validateTicketChannelCondition(ticketChannelFromModel(condition));
    case Condition.TOPIC:
      return validateTopicCondition(topicFromModel(condition));
    case Condition.DAMAGED:
      return validateDamagedCondition(damagedFromModel(condition));
    case Condition.IS_REPAIR:
      return validateIsRepairCondition(isRepairFromModel(condition));
    case Condition.WRONG_PRODUCT:
      return validateWrongProductCondition(wrongProductFromModel(condition));
    case Condition.IS_REFUND_RETURN:
      return validateIsRefundReturnCondition(
        isRefundReturnFromModel(condition),
      );
    case Condition.TRACKING_TYPE:
      return validateTrackingTypeCondition(trackingTypeFromModel(condition));
    case Condition.ORDER_COUNT_GREATER_THAN:
      return validateOrderCountGreaterThanCondition(
        orderCountGreaterThanFromModel(condition),
      );
    case Condition.TOTAL_SPENT_GREATER_THAN:
      return validateTotalSpentGreaterThanCondition(
        totalSpentGreaterThanFromModel(condition),
      );
    case Condition.FINAL_SALE_RETURN:
      return validateFinalSaleReturnCondition(
        finalSaleReturnFromModel(condition),
      );
    case Condition.DISCOUNT:
      return validateDiscountCondition(discountFromModel(condition));
    case Condition.ITEM_DISCOUNT:
      return validateItemDiscountCondition(itemDiscountFromModel(condition));
    case Condition.LOYALTY_EARNED:
      return validateLoyaltyEarnedCondition(loyaltyEarnedFromModel(condition));
    case Condition.SKU:
      return validateSkuCondition(skuFromModel(condition));
    case Condition.ADJUSTMENT_AMOUNT:
      return validateAdjustmentAmountCondition(
        adjustmentAmountFromModel(condition),
      );
    case Condition.SUBJECT_LINE:
      return validateSubjectLineCondition(subjectLineFromModel(condition));
    case Condition.ASSIGNMENT_TYPE:
      return validateAssignmentTypeCondition(
        assignmentTypeFromModel(condition),
      );
    case Condition.CHANNEL:
      return validateChannelCondition(channelFromModel(condition));
    case Condition.PLATFORM:
      return validatePlatformCondition(platformFromModel(condition));
    case Condition.PAYMENT_METHOD:
      return validatePaymentMethodCondition(paymentMethodFromModel(condition));
    case Condition.COMPENSATION_METHOD:
      return validateCompensationMethodCondition(
        compensationMethodFromModel(condition),
      );
    case Condition.RETURN_METHOD:
      return validateReturnMethodCondition(returnMethodFromModel(condition));
    case Condition.PURCHASE_CHANNEL:
      return validatePurchaseChannelCondition(
        purchaseChannelConditionFromModel(condition),
      );
    case Condition.RECEIVED_AT_EMAIL:
      return validateReceivedAtEmailCondition(
        receivedAtEmailFromModel(condition),
      );
    case Condition.SENT_FROM_EMAIL:
      return validateSentFromEmailCondition(sentFromEmailFromModel(condition));
    case Condition.MESSAGE_BODY_MATCHES:
      return validateMessageBodyMatchesCondition(
        messageBodyMatchesFromModel(condition),
      );
    case Condition.DELIVERY_WITHIN:
      return validateDeliveryWithinCondition(
        deliveryWithinFromModel(condition),
      );
    case Condition.FULFILLMENT_WITHIN:
      return validateFulfillmentWithinCondition(
        fulfillmentWithinFromModel(condition),
      );
    case Condition.ORDER_WITHIN:
      return validateOrderWithinCondition(orderWithinFromModel(condition));
    case Condition.FULFILLMENT_TRACKING_UPDATE_WITHIN:
      return validateFulfillmentTrackingUpdateWithinCondition(
        fulfillmentTrackingUpdateWithinFromModel(condition),
      );
    case Condition.EXCHANGE_COUNT:
      return validateExchangeCountCondition(exchangeCountFromModel(condition));
    case Condition.ITEMS_BEING_RETURNED_COUNT:
      return validateItemsBeingReturnedCountCondition(
        itemsBeingReturnedCountFromModel(condition),
      );
    case Condition.CUSTOMER_COUNTRY:
      return validateCustomerCountryCondition(
        customerCountryFromModel(condition),
      );
    case Condition.CUSTOMER_ADDRESS:
      return validateCustomerAddressCondition(
        customerAddressFromModel(condition),
      );
    case Condition.ORDER_DATE:
      return validateDateConditionState(orderDateFromModel(condition));
    case Condition.RETURN_DATE:
      return validateDateConditionState(returnDateFromModel(condition));
    case Condition.LOCATION_NAME:
      return validateLocationNameCondition(locationNameFromModel(condition));
    case Condition.MULTIPLE_CHOICE:
      return validateMultipleChoiceCondition(
        multipleChoiceConditionFromModel(condition),
      );
    case Condition.PRICE:
    case Condition.NON_RETURNED_VALUE:
    case Condition.RETURN_VALUE:
    case Condition.TOTAL_RETURN_VALUE:
    case Condition.TOTAL_PRE_DISCOUNT_PRICE:
      return validatePriceCondition(priceConditionFromModel(condition));
    case Condition.NUM_CUSTOMER_CLAIMS:
      return validateNumCustomerClaimsCondition(
        numCustomerClaimsFromModel(condition),
      );
    case Condition.NUM_CUSTOMER_RETURNS:
      return validateNumCustomerReturnsCondition(
        numCustomerReturnsFromModel(condition),
      );
    case Condition.NUM_ORDERS:
      return validateNumOrdersCondition(numOrdersFromModel(condition));
    case Condition.RETURN_RATE:
      return validateReturnRateCondition(returnRateFromModel(condition));
    case Condition.RETURN_REASON:
      return validateReturnReasonCondition(returnReasonFromModel(condition));
    case Condition.RETURN_TYPE:
      return validateReturnTypeCondition(
        returnTypeConditionFromModel(condition),
      );
    case Condition.PRODUCT_VARIANT_METAFIELD:
      return validateProductVariantMetafieldCondition(
        productVariantMetafieldFromModel(condition),
      );
    default:
      return assertNever(condition);
  }
}
